import { Pressable, View } from 'react-native';

import { Badge, Button, Text } from '@/components/ui';
import { assignAdminLlmProfile } from '@/lib/admin/llm-models-api';
import {
  formatCount,
  formatPricePer1M,
  retentionSummary,
  type AdminLlmModel,
  type AdminLlmProfile,
  type LlmCatalogCandidate,
  type LlmCatalogResponse,
  type LlmProfileId,
  type LlmProfileSuggestion,
} from '@/lib/admin/llm-models-types';

export type LlmProfileChoice = {
  profile: LlmProfileId;
  modelId: string;
  name: string;
  allowlistId: string | null;
  enabled: boolean;
  privacy: LlmCatalogCandidate['privacy'];
};

type LlmControlProfilesProps = {
  models: AdminLlmModel[];
  profiles: AdminLlmProfile[];
  catalog: LlmCatalogResponse | null;
  usingKey: string | null;
  onUse: (choice: LlmProfileChoice) => void;
  onChange: (profiles: AdminLlmProfile[]) => void;
  onError: (message: string | null) => void;
};

function suggestionStats(candidate: LlmCatalogCandidate | undefined): string {
  if (!candidate) return '';
  const latency =
    candidate.latencyP50Sec == null ? null : `${Math.round(candidate.latencyP50Sec * 1000)} ms`;
  return [
    retentionSummary(candidate.privacy),
    candidate.throughputP50 == null ? null : `${formatCount(candidate.throughputP50)} tok/s`,
    latency,
    `${formatPricePer1M(candidate.promptPer1M)} / ${formatPricePer1M(candidate.completionPer1M)} per 1M`,
  ]
    .filter(Boolean)
    .join(' · ');
}

function routeLine(model: AdminLlmModel): string {
  const pinned = model.openrouterMetadata?.endpoints.find(
    (endpoint) => endpoint.tag === model.providerTag && endpoint.isZdr,
  );
  if (pinned) return `Privacy route: ${pinned.providerName} (zero retention, no fallback)`;
  if (model.providerTag)
    return 'Privacy route: pinned provider. Refresh metadata if the name is missing.';
  return 'Privacy route: any zero-retention provider';
}

export function LlmControlProfiles({
  models,
  profiles,
  catalog,
  usingKey,
  onUse,
  onChange,
  onError,
}: LlmControlProfilesProps) {
  const enabled = models.filter((model) => model.enabled);

  async function assignRegistered(profile: AdminLlmProfile, model: AdminLlmModel) {
    onError(null);
    try {
      onChange(await assignAdminLlmProfile(profile.profile, model.id));
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to assign profile');
    }
  }

  return (
    <View className="mt-6 flex flex-col gap-4">
      {profiles.map((profile) => {
        const suggestions = catalog?.suggestions[profile.profile] ?? [];
        return (
          <View key={profile.profile} className="rounded-lg border border-border p-4">
            <Text variant="h3">{profile.label}</Text>
            <Text variant="body-sm" className="mt-1 text-muted">
              {profile.summary}
            </Text>

            <View className="mt-3 rounded-lg bg-surface-raised p-3">
              <Text variant="caption" className="text-muted">
                Current model
              </Text>
              <Text variant="body" className="mt-1">
                {profile.model ? profile.model.displayName : 'No model assigned'}
              </Text>
              {profile.model ? (
                <>
                  <Text variant="caption" className="text-muted">
                    {profile.model.openrouterModelId}
                  </Text>
                  <Text variant="caption" className="mt-1 text-muted">
                    {routeLine(profile.model)}
                  </Text>
                  <Text variant="caption" className="mt-1 text-muted">
                    {retentionSummary(profile.model.openrouterMetadata?.privacy)}
                  </Text>
                </>
              ) : null}
            </View>

            <Text variant="body-sm" className="mt-4 font-medium">
              From OpenRouter top lists
            </Text>
            {suggestions.length === 0 ? (
              <Text variant="caption" className="mt-1 text-muted">
                Refresh the catalogue to load the top models for {profile.label}.
              </Text>
            ) : (
              suggestions.map((suggestion) => (
                <SuggestionRow
                  key={suggestion.modelId}
                  profile={profile}
                  suggestion={suggestion}
                  candidate={catalog?.candidates.find((row) => row.modelId === suggestion.modelId)}
                  busy={usingKey === `${profile.profile}:${suggestion.modelId}`}
                  onUse={onUse}
                />
              ))
            )}

            {enabled.length > 0 ? (
              <>
                <Text variant="body-sm" className="mt-4 font-medium">
                  Registered models
                </Text>
                <View className="mt-2 flex-row flex-wrap gap-2">
                  {enabled.map((model) => {
                    const selected = profile.model?.id === model.id;
                    return (
                      <Pressable
                        key={model.id}
                        disabled={selected}
                        onPress={() => void assignRegistered(profile, model)}
                      >
                        <Badge
                          label={selected ? `${model.displayName} · current` : model.displayName}
                          variant={selected ? 'accent' : 'neutral'}
                        />
                      </Pressable>
                    );
                  })}
                </View>
              </>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

function SuggestionRow({
  profile,
  suggestion,
  candidate,
  busy,
  onUse,
}: {
  profile: AdminLlmProfile;
  suggestion: LlmProfileSuggestion;
  candidate: LlmCatalogCandidate | undefined;
  busy: boolean;
  onUse: (choice: LlmProfileChoice) => void;
}) {
  const current = profile.model?.openrouterModelId === suggestion.modelId;
  const name = suggestion.displayName ?? candidate?.name ?? suggestion.modelId;
  return (
    <View className="mt-2 rounded border border-border p-3">
      <Text variant="body-sm">{name}</Text>
      <Text variant="caption" className="text-muted">
        {suggestion.modelId}
        {suggestion.enabled ? ' · enabled' : ''}
      </Text>
      <Text variant="caption" className="mt-1 text-muted">
        {suggestionStats(candidate)}
      </Text>
      <View className="mt-2 self-start">
        <Button
          label={current ? 'Current' : busy ? 'Assigning…' : `Use for ${profile.label}`}
          disabled={current}
          loading={busy}
          size="sm"
          variant={current ? 'secondary' : 'primary'}
          onPress={() =>
            onUse({
              profile: profile.profile,
              modelId: suggestion.modelId,
              name,
              allowlistId: suggestion.allowlistId,
              enabled: suggestion.enabled,
              privacy: candidate?.privacy ?? null,
            })
          }
        />
      </View>
    </View>
  );
}
